// feature/insumos-pipeline.
// Per César's screenshot feedback: "this screen needs a tiny ui overhaul,
// it is not separating into the insumos already asked for and the ones
// they have done … they dont need to see the favorables no favorables
// etc etc it is irrelevant to them right now."
//
// Three changes vs the pre-Sprint-57 version:
//   1. Fallos excluded server-side (insumos.pipeline endpoint, Sprint 35
//      already routes fallos directly to Molly — frontend filter belt-
//      and-suspenders).
//   2. Single mixed-estado table replaced with two sections:
//        Pendientes de solicitar  (estado=pendiente_insumos)
//        Solicitud enviada        (insumos_solicitados / expediente_solicitado)
//      Sprint 56 removed insumos_recibidos as a held state — Camilo's
//      upload auto-transitions, so nothing dwells there long enough to
//      surface here.
//   3. Page title "Mi cola" → "Pipeline de insumos" with subtitle
//      explaining the two phases. Sprint 55c EstadoFilterPills (Activos
//      / Cerrados / Todos) sits above both sections.

import React, { useEffect, useState } from "react";
import { insumos } from "../../api/endpoints";
import Spinner from "../../components/Spinner";
import Empty from "../../components/Empty";
import { TipoPill, EstadoPill, TipoFalloPill } from "../../components/Pills";
import EstadoFilterPills, { filterByActiveStatus } from "../../components/EstadoFilterPills";
import CaseDetail from "../case-detail";
import { useRefreshOnNotification } from "../../components/useRefreshOnNotification";

function rowTint(d) {
  if (d > 4) return { background: "var(--color-danger-bg)" };
  if (d > 2) return { background: "var(--color-warning-bg)" };
  return undefined;
}

export default function MyQueue() {
  const [counter, setCounter] = useState(null);
  const [data, setData] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [estadoFilter, setEstadoFilter] = useState("activos");
  // Sprint 57b — César wanted real tabs, not stacked sections. Default
  // to "para_enviar" since that's the actionable list; "enviados" is
  // passive monitoring.
  const [activeTab, setActiveTab] = useState("para_enviar");
  // Sprint 57d — sort controls per tab. Default to oldest-first by
  // fecha_ingreso (matches the backend's natural order). User can flip
  // to case_number A-Z / Z-A or days asc/desc for triage.
  const [sortBy, setSortBy] = useState("fecha_asc");

  function load() {
    insumos.myCounter().then(r => setCounter(r.data)).catch(() => setCounter(null));
    setData(null);
    insumos.pipeline(true)
      .then((r) => setData(r.data?.cases || []))
      .catch(() => setData([]));
  }
  useEffect(() => { load(); }, []);
  useRefreshOnNotification(["insumo_assigned", "insumo_stolen", "insumo_response"], load);

  const filtered = filterByActiveStatus(data || [], estadoFilter);
  const pendientes = filtered.filter((c) => c.estado === "pendiente_insumos");
  const enviadas = filtered.filter((c) =>
    c.estado === "insumos_solicitados" || c.estado === "expediente_solicitado"
  );
  const sortedCases = sortCases(activeTab === "para_enviar" ? pendientes : enviadas, sortBy);
  const visibleCases = sortedCases;

  return (
    <div className="col" style={{ gap: 16 }}>
      <div>
        <h1 style={{ margin: 0 }}>Flujo de insumos</h1>
        <div className="muted" style={{ marginTop: 4, fontSize: 13 }}>
          Su cola de casos: pendientes de solicitar y solicitudes ya enviadas
          esperando respuesta de Previsora.
        </div>
      </div>

      <div className="grid-3">
        <div className="card tight">
          <div className="label-uc">Hoy</div>
          <div style={{ fontSize: 32, fontWeight: 600, color: "var(--text-primary)", margin: "8px 0 0" }}>
            {counter == null ? <Spinner /> : `${counter.today} / ${counter.quota}`}
          </div>
          <div className="muted" style={{ fontSize: 12 }}>
            Lote actual del equipo: #{counter?.batch_index_now ?? "—"}
          </div>
        </div>
        <div className="card tight">
          <div className="label-uc">Mi cola abierta</div>
          <div style={{ fontSize: 32, fontWeight: 600, color: "var(--text-primary)", margin: "8px 0 0" }}>
            {counter == null ? <Spinner /> : counter.open_queue}
          </div>
          <div className="muted" style={{ fontSize: 12 }}>Casos pendientes de cierre</div>
        </div>
        <div className="card tight">
          <div className="label-uc">Cola del equipo hoy</div>
          <div style={{ fontSize: 32, fontWeight: 600, color: "var(--text-primary)", margin: "8px 0 0" }}>
            {counter == null ? <Spinner /> : counter.team_today}
          </div>
          <div className="muted" style={{ fontSize: 12 }}>Total recibidos hoy</div>
        </div>
      </div>

      <EstadoFilterPills cases={data || []} value={estadoFilter} onChange={setEstadoFilter} />

      {/* Sprint 57b — tabs replace the stacked sections from Sprint 57. */}
      <PipelineTabs
        active={activeTab}
        onChange={setActiveTab}
        paraEnviarCount={pendientes.length}
        enviadasCount={enviadas.length}
      />

      {data == null ? (
        <div className="card" style={{ padding: 32, textAlign: "center" }}><Spinner size={20} /></div>
      ) : !data.length ? (
        <div className="card">
          <Empty
            title="No tiene casos asignados hoy"
            message="Cuando Reparto cree un caso que necesite insumos, aparecerá aquí."
          />
        </div>
      ) : visibleCases.length === 0 ? (
        <div className="card">
          <Empty
            title={activeTab === "para_enviar"
              ? "No hay casos pendientes de solicitar"
              : "No hay solicitudes enviadas pendientes"}
            message={activeTab === "para_enviar"
              ? "Cuando Reparto cree un caso que necesite insumos, aparecerá aquí."
              : "Cuando envíe una solicitud a Previsora aparecerá aquí hasta que Camilo cargue la respuesta."}
          />
        </div>
      ) : (
        <>
          <SortControl value={sortBy} onChange={setSortBy} />
          <PipelineTable cases={visibleCases} onOpen={setOpenId} />
        </>
      )}

      <CaseDetail caseId={openId} open={!!openId} defaultTab="insumos"
                  onClose={() => setOpenId(null)} onChanged={load} />
    </div>
  );
}

// Sprint 57b — underline-active tab strip matching Camilo's
// Sprint 37c dispatcher chip pattern. Helper text under the tabs
// explains the active view.
function PipelineTabs({ active, onChange, paraEnviarCount, enviadasCount }) {
  const helper = active === "para_enviar"
    ? "Casos que necesitan que genere y envíe la solicitud de insumos a Previsora."
    : "Solicitud ya enviada. Esperando respuesta — Camilo carga la respuesta cuando llegue.";
  return (
    <div>
      <div role="tablist" style={{
        display: "flex", gap: 4, flexWrap: "wrap",
        borderBottom: "1px solid var(--border-hairline)",
        marginTop: 4,
      }}>
        <TabButton
          label="Para enviar"
          icon="⏳"
          count={paraEnviarCount}
          active={active === "para_enviar"}
          onClick={() => onChange("para_enviar")}
        />
        <TabButton
          label="Enviados"
          icon="📤"
          count={enviadasCount}
          active={active === "enviados"}
          onClick={() => onChange("enviados")}
        />
      </div>
      <div className="muted" style={{ fontSize: 12, padding: "10px 0 4px" }}>{helper}</div>
    </div>
  );
}

function TabButton({ label, icon, count, active, onClick }) {
  return (
    <button type="button" role="tab" aria-selected={active}
            onClick={onClick}
            style={{
              display: "inline-flex", alignItems: "center", gap: 8,
              padding: "10px 16px",
              background: "transparent",
              border: "none",
              borderBottom: `2px solid ${active ? "var(--accent-mint)" : "transparent"}`,
              fontSize: 13,
              fontWeight: active ? 600 : 500,
              color: active ? "var(--accent-mint)" : "var(--text-muted)",
              cursor: "pointer",
              marginBottom: -1,
            }}>
      <span aria-hidden="true">{icon}</span>
      <span>{label}</span>
      <span style={{
        background: active ? "var(--accent-mint-bg)" : "var(--bg-subtle)",
        color: active ? "var(--accent-mint)" : "var(--text-muted)",
        fontSize: 10, fontWeight: 600,
        padding: "1px 8px", borderRadius: 10,
        minWidth: 18, textAlign: "center",
      }}>{count}</span>
    </button>
  );
}

// Sprint 57d — small select above the table for case_number / days
// sorts. Defaults to fecha_asc (oldest-first) which matches the
// backend's natural priority order. case_number A-Z works because
// case_numbers are formatted TUT-YYYY-####, so lexicographic sort
// inside a year matches numeric sort.
const SORT_OPTIONS = [
  { value: "fecha_asc",  label: "Más antiguos primero" },
  { value: "fecha_desc", label: "Más recientes primero" },
  { value: "case_asc",   label: "Caso A → Z" },
  { value: "case_desc",  label: "Caso Z → A" },
  { value: "dias_desc",  label: "Más días en trámite" },
  { value: "dias_asc",   label: "Menos días en trámite" },
];

function sortCases(rows, sortBy) {
  if (!rows || !rows.length) return rows;
  const out = [...rows];
  switch (sortBy) {
    case "case_asc":
      out.sort((a, b) => String(a.case_number).localeCompare(String(b.case_number)));
      break;
    case "case_desc":
      out.sort((a, b) => String(b.case_number).localeCompare(String(a.case_number)));
      break;
    case "dias_desc":
      out.sort((a, b) => (b.dias_en_tramite ?? 0) - (a.dias_en_tramite ?? 0));
      break;
    case "dias_asc":
      out.sort((a, b) => (a.dias_en_tramite ?? 0) - (b.dias_en_tramite ?? 0));
      break;
    case "fecha_desc":
      out.sort((a, b) => new Date(b.fecha_ingreso) - new Date(a.fecha_ingreso));
      break;
    case "fecha_asc":
    default:
      out.sort((a, b) => new Date(a.fecha_ingreso) - new Date(b.fecha_ingreso));
      break;
  }
  return out;
}

function SortControl({ value, onChange }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 8,
      padding: "8px 0", marginBottom: 4,
    }}>
      <label className="muted" style={{
        margin: 0, fontSize: 12, textTransform: "none", letterSpacing: 0,
      }}>
        Ordenar:
      </label>
      <select value={value} onChange={(e) => onChange(e.target.value)}
              style={{ width: "auto", padding: "4px 8px", fontSize: 12 }}>
        {SORT_OPTIONS.map((opt) => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
    </div>
  );
}

function PipelineTable({ cases, onOpen }) {
  return (
    <div className="card" style={{ padding: 0 }}>
      <table>
        <thead>
          <tr>
            <th>Caso</th><th>Tipo de actuación</th><th>Accionante</th><th>Radicado</th>
            <th>Juzgado</th><th>Días en trámite</th><th>Estado</th>
          </tr>
        </thead>
        <tbody>
          {cases.map((c) => {
            const d = c.dias_en_tramite ?? 0;
            return (
              <tr key={c.id} style={{ cursor: "pointer", ...(rowTint(d) || {}) }}
                  onClick={() => onOpen(c.id)}>
                <td><strong>{c.case_number}</strong></td>
                <td>
                  <TipoPill tipo={c.tipo} />
                  {/* feature/insumos-pipeline-fallos. Fallos
                      now appear in the pipeline (Sprint 58d intent restored).
                      Show the resultado at a glance via the Sprint 58e
                      TipoFalloPill — same component used on Molly's /fallos,
                      MisCasos rows, CaseDetail header, etc., so the design
                      language stays consistent. */}
                  {c.tipo === "fallo" && c.tipo_fallo && (
                    <span style={{ marginLeft: 6 }}>
                      <TipoFalloPill value={c.tipo_fallo} />
                    </span>
                  )}
                </td>
                <td>{c.accionante || "—"}</td>
                <td style={{ fontFamily: "ui-monospace, 'SF Mono', Menlo, Consolas, monospace", fontSize: 11 }}>
                  {c.radicado || "—"}
                </td>
                <td>{c.juzgado || "—"}</td>
                <td>{d}</td>
                <td><EstadoPill c={c} /></td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
